import React, { useCallback, useEffect, useState } from 'react'
import IncidentsCountWmrcGroupTable from './Table/IncidentsCountWmrcGroupTable'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import { useLocation, useSearchParams } from 'react-router-dom'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import axios from 'axios'
import { useSelector } from 'react-redux'
import toasters from '../../../../../../../utils/toasters/Toasters'

const IncidentCountWmrcGroupReport = () => {
    const location = useLocation()
    const { token } = useSelector((state) => state.auth)

    const [searchParams] = useSearchParams();

  // Table options & sort with sessionStorage fallback
  const storedTableOptions =
    JSON.parse(sessionStorage.getItem("incidentCountWmrcGroupOptions")) || {
      rowPerPages: 10,
      activePage: 0,
    };
  const storedSort =
    JSON.parse(sessionStorage.getItem("incidentCountWmrcGroupSort")) || null;

  const [tableOptions, setTableOptions] = useState(storedTableOptions);
  const [sort, setSort] = useState(storedSort);

  useEffect(() => {
    sessionStorage.setItem(
      "incidentCountWmrcGroupOptions",
      JSON.stringify(tableOptions)
    );
  }, [tableOptions]);

  useEffect(() => {
    sessionStorage.setItem(
      "incidentCountWmrcGroupSort",
      JSON.stringify(sort)
    );
  }, [sort]);

  // Payload initialization from location.state → sessionStorage → URL params
  const [payload, setPayload] = useState(() => {
    if (location.state) return location.state;

    const stored = sessionStorage.getItem("incidentCountWmrcGroupPayload");
    if (stored) return JSON.parse(stored);

    const params = Object.fromEntries([...searchParams]);
    return Object.keys(params).length ? params : null;
  });

  useEffect(() => {
    if (location.state) {
      const prevStored = sessionStorage.getItem(
        "incidentCountWmrcGroupPayload"
      );
      const prev = prevStored ? JSON.parse(prevStored) : null;

      if (JSON.stringify(prev) !== JSON.stringify(location.state)) {
        sessionStorage.setItem(
          "incidentCountWmrcGroupPayload",
          JSON.stringify(location.state)
        );
        setPayload(location.state);
      }
    } else if (!payload) {
      // fallback to URL query params
      const params = Object.fromEntries([...searchParams]);
      if (Object.keys(params).length) {
        sessionStorage.setItem(
          "incidentCountWmrcGroupPayload",
          JSON.stringify(params)
        );
        setPayload(params);
      }
    }
  }, [location.state, searchParams]);

    const getIncidentCountWmrcGroupReport = useCallback(async () => {
        let apiQueryParams = {
            per_page: tableOptions.rowPerPages,
            page: tableOptions.activePage + 1,
            export: 'N',
        }

        if (sort?.columnName && sort.order) {
            apiQueryParams.order = sort.order
            apiQueryParams.sort = sort.columnName
        }

        const cleanedPayload = { ...payload }
        if (cleanedPayload.status === 'ALL') delete cleanedPayload.status

        return fetchIncidentAnalysisData({
            ...cleanedPayload,
            ...apiQueryParams,
            code: 'incidentCountWMRCGroup',
            export: 'N',
        })
    }, [payload, sort, tableOptions])

    const { data, isLoading, isFetching, isError, error } = useQuery(
        ['fetch-incident-count-wmrc-group', payload, sort, tableOptions],
        getIncidentCountWmrcGroupReport,
        { enabled: !!payload }
    )

    const handlePageClick = (event) => {
        setTableOptions((prev) => ({
            ...prev,
            activePage: event.selected,
        }))
    }

    const handleRowsPerPage = (e) => {
        setTableOptions({
            rowPerPages: e.target.value,
            activePage: 0,
        })
    }

    const handleColumnSort = (column, sortDirection) => {
        setSort({
            columnName: column.sortField,
            order: sortDirection,
        })
    }

    const downloadPDF = async () => {
        try {
            const cleanedPayload = { ...payload }
            if (cleanedPayload.status === 'ALL') delete cleanedPayload.status

            // Fetch full data
            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                {
                    ...cleanedPayload,
                    code: 'incidentCountWMRCGroup',
                    export: 'N',
                    pagination: 'false',
                    //   order: sort?.order || '',
                    //   sort: sort?.columnName || '',
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            )

            const fullData = response?.data?.data

            if (!Array.isArray(fullData) || fullData.length === 0) {
                toasters({
                    type: 'error',
                    message: 'No data found for PDF export',
                })
                return
            }

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
            })

            doc.setFontSize(18)
            doc.text('Incident Count WMRC Group Report', 14, 15)

            const tableColumns = [
                { header: 'Group ID', dataKey: 'group_id' },
                { header: 'Congregation', dataKey: 'group_name' },
                { header: 'WMRC Group', dataKey: 'wmrc_group' },
                {
                    header: 'WMRC Name',
                    dataKey: 'wmrc_group_contact_name',
                },
                { header: 'Status', dataKey: 'ref_status_cd' },
                { header: 'Year-Month', dataKey: 'yearmonth' },
                { header: 'Incident Count', dataKey: 'incident_count' },
            ]

            const tableRows = fullData.map((row) => ({
                group_id: row.group_id || '',
                group_name: row.group_name || '',
                wmrc_group: row.wmrc_group || '',
                wmrc_group_contact_name: row.wmrc_group_contact_name || '',
                ref_status_cd: row.ref_status_cd || '',
                yearmonth: row.yearmonth || '',
                incident_count: row.incident_count || 0,
            }))

            doc.autoTable({
                columns: tableColumns,
                body: tableRows,
                startY: 25,
                styles: { fontSize: 10, cellPadding: 3 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { left: 10, right: 10 },
            })

            const totalIncidents = fullData.reduce(
                (sum, item) => sum + (item.incident_count || 0),
                0
            )

            const finalY = doc.lastAutoTable.finalY + 10
            doc.text(`Total Incidents: ${totalIncidents}`, 14, finalY)

            doc.save('IncidentCountWMRCGroupReport.pdf')
             return doc.output('blob')
        } catch (error) {
            toasters({
                type: 'error',
                message:
                    error?.response?.data?.message || 'Failed to generate PDF',
            })
        }
    }

    const ExcelIncidentCountWmrcGroupReport = async () => {
        try {
            const cleanedPayload = { ...payload }
            if (cleanedPayload.status === 'ALL') delete cleanedPayload.status

            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                {
                    ...cleanedPayload,
                    code: 'incidentCountWMRCGroup',
                    export: 'Y',
                    pagination: 'false',
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            )

            if (!response?.data || typeof response.data !== 'string') {
                toasters({ type: 'error', message: 'No data found to export' })
                return
            }

            const blob = new Blob([response.data], {
                type: 'text/csv;charset=utf-8;',
            })

            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'IncidentCountWMRCGroupReport.csv'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (error) {
            toasters({
                type: 'error',
                message:
                    error?.response?.data?.message ||
                    'Error downloading Excel report',
            })
        }
    }

    const pageCount = Math.ceil((data?.total || 0) / tableOptions.rowPerPages)

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <IncidentsCountWmrcGroupTable
                    isLoading={isLoading || isFetching}
                    data={data?.data || []}
                    totaldata={data?.total}
                    handleColumnSort={handleColumnSort}
                    handleRowsPerPage={handleRowsPerPage}
                    handlePageClick={handlePageClick}
                    pageCount={pageCount}
                    tableOptions={tableOptions}
                    downloadPDF={downloadPDF}
                    downloadExcel={ExcelIncidentCountWmrcGroupReport}
                    reportTableData={data}
                     reportCode={payload?.code}
                />
            )}
        </>
    )
}

export default IncidentCountWmrcGroupReport
